import { defineStore } from "pinia";
import {
  collection,
  getDocs,
  getCountFromServer,
  limit,
  orderBy,
  onSnapshot,
  query,
  startAfter,
  where,
  Unsubscribe,
  QueryDocumentSnapshot,
  QueryConstraint,
  DocumentData,
} from "firebase/firestore";
import { firestore } from "@/firebase";
import {
  WithdrawalRequest,
  WithdrawalStatus,
} from "@shared/entities/Vault/VaultInterfaces";

export type WithdrawalRequestUI = WithdrawalRequest & {
  dateCreated: Date;
  dateUpdated: Date;
  authorizedAt?: Date;
  rejectedAt?: Date;
  canceledAt?: Date;
  requiredApprovals?: number;
  allowedApprovers?: string[];
  approvals?: { by: string; at: Date }[];
};

const STATUS_FILTER: WithdrawalStatus[] = [
  "pending",
  "authorized",
  "ready",
  "rejected",
  "canceled",
  "failed",
];

const toDate = (value: any): Date => {
  if (!value) return new Date(NaN);
  if (value instanceof Date) return value;
  if (typeof value === "number") return new Date(value);
  if (typeof value === "string") return new Date(value);
  if (typeof value === "object" && typeof value.toDate === "function") {
    return value.toDate();
  }
  return new Date(value);
};

const mapDoc = (
  docSnap: QueryDocumentSnapshot<DocumentData>
): WithdrawalRequestUI => {
  const data = docSnap.data() as Partial<WithdrawalRequest>;
  return {
    _id: docSnap.id,
    vaultId: data.vaultId || "",
    concept: data.concept || "",
    note: data.note,
    createdBy: data.createdBy || "",
    dateCreated: toDate(data.dateCreated),
    dateUpdated: toDate(data.dateUpdated),
    status: (data.status as WithdrawalStatus) || "pending",
    authorizedBy: data.authorizedBy,
    authorizedAt: data.authorizedAt ? toDate(data.authorizedAt) : undefined,
    rejectedBy: data.rejectedBy,
    rejectedAt: data.rejectedAt ? toDate(data.rejectedAt) : undefined,
    canceledBy: data.canceledBy,
    canceledAt: data.canceledAt ? toDate(data.canceledAt) : undefined,
    destinations: Array.isArray(data.destinations) ? data.destinations : [],
    totalUsd: Number(data.totalUsd || 0),
    totalBtc: Number(data.totalBtc || 0),
    feeSpeed: data.feeSpeed,
    feeBtc: data.feeBtc,
    feeUsd: data.feeUsd,
    feeBtcPrice: data.feeBtcPrice,
    txHash: data.txHash,
    transactionRequestId: data.transactionRequestId,
    requiredApprovals: data.requiredApprovals,
    allowedApprovers: Array.isArray(data.allowedApprovers)
      ? data.allowedApprovers
      : [],
    approvals: Array.isArray(data.approvals)
      ? data.approvals.map((a) => ({
          by: a.by,
          at: toDate(a.at),
        }))
      : [],
    txStatus: data.txStatus,
    btcPriceAtBroadcast: data.btcPriceAtBroadcast,
  };
};

let sentCursors: Array<QueryDocumentSnapshot<DocumentData> | undefined> = [];

export const useWithdrawalRequestsStore = defineStore("withdrawalRequests", {
  state: () => ({
    items: [] as WithdrawalRequestUI[],
    sentItems: [] as WithdrawalRequestUI[],
    loading: false,
    unsubscribe: undefined as Unsubscribe | undefined,
    unsubscribeSent: undefined as Unsubscribe | undefined,
    // Pagination for sent requests
    sentPage: 0,
    sentPageSize: 15,
    sentHasMore: false,
    sentTotalCount: 0,
    sentLoading: false,
    // Filters
    sentFilterYear: new Date().getFullYear(),
    sentFilterMonth: new Date().getMonth(),
  }),
  getters: {
    pendingRequests: (state): WithdrawalRequestUI[] => state.items,
    sentRequests: (state): WithdrawalRequestUI[] => state.sentItems,
    isLoading: (state): boolean => state.loading || state.sentLoading,
    isSentLoading: (state): boolean => state.sentLoading,
  },
  actions: {
    stopListening() {
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = undefined;
      }
    },
    stopListeningSent() {
      if (this.unsubscribeSent) {
        this.unsubscribeSent();
        this.unsubscribeSent = undefined;
      }
    },

    listenPendingRequests() {
      this.stopListening();
      this.loading = true;
      const colRef = collection(firestore, "systemWithdrawalRequests");
      const q = query(colRef, where("status", "in", STATUS_FILTER));

      this.unsubscribe = onSnapshot(
        q,
        (snap) => {
          this.items = snap.docs
            .map((docSnap) =>
              mapDoc(docSnap as QueryDocumentSnapshot<DocumentData>)
            )
            .sort(
              (a, b) =>
                (b.dateCreated?.getTime?.() || 0) -
                (a.dateCreated?.getTime?.() || 0)
            );
          this.loading = false;
        },
        (error) => {
          console.error("Error listening withdrawal requests", error);
          this.loading = false;
        }
      );
    },

    async fetchSentRequests(
      params: {
        size?: number;
        force?: boolean;
        page?: number;
        year?: number;
        month?: number;
      } = {}
    ) {
      const pageSize = params.size ?? this.sentPageSize ?? 15;
      const targetPage = Math.max(0, params.page ?? this.sentPage ?? 0);
      const force = params.force === true;

      // Update filters if provided
      if (params.year !== undefined) this.sentFilterYear = params.year;
      if (params.month !== undefined) this.sentFilterMonth = params.month;

      if (this.sentLoading && !force) {
        return;
      }

      this.stopListeningSent();
      const colRef = collection(firestore, "systemWithdrawalRequests");
      let qBuilder = query(colRef, where("status", "==", "sent"));

      // Apply date filters
      const start = new Date(this.sentFilterYear, this.sentFilterMonth, 1);
      const end = new Date(this.sentFilterYear, this.sentFilterMonth + 1, 1);
      qBuilder = query(
        qBuilder,
        where("dateCreated", ">=", start),
        where("dateCreated", "<", end)
      );

      const baseQuery = qBuilder;

      if (targetPage === 0) {
        sentCursors = [];
      }

      const ensureCursorForPage = async (page: number) => {
        if (page <= 0) return undefined;
        if (sentCursors[page - 1]) return sentCursors[page - 1];

        let nearestKnown = -1;
        for (let i = sentCursors.length - 1; i >= 0; i -= 1) {
          if (sentCursors[i]) {
            nearestKnown = i + 1;
            break;
          }
        }

        let currentPage = Math.max(0, nearestKnown);
        let cursorRef =
          currentPage > 0 ? sentCursors[currentPage - 1] : undefined;

        while (currentPage < page) {
          const constraints: QueryConstraint[] = [];
          constraints.push(orderBy("dateCreated", "desc"));
          if (cursorRef) {
            constraints.push(startAfter(cursorRef));
          }
          constraints.push(limit(pageSize));

          const snap = await getDocs(query(baseQuery, ...constraints));
          const docs = snap.docs;
          sentCursors[currentPage] =
            docs.length > 0
              ? (docs[docs.length - 1] as QueryDocumentSnapshot<DocumentData>)
              : undefined;
          cursorRef = sentCursors[currentPage];
          currentPage += 1;
          if (!cursorRef) break;
        }

        return sentCursors[page - 1];
      };

      this.sentLoading = true;
      try {
        const cursor = await ensureCursorForPage(targetPage);

        const constraints: QueryConstraint[] = [];
        constraints.push(orderBy("dateCreated", "desc"));
        if (cursor) {
          constraints.push(startAfter(cursor));
        }
        constraints.push(limit(pageSize));

        const baseQueryWithConstraints = query(baseQuery, ...constraints);

        const countSnapshot = await getCountFromServer(baseQuery);
        this.sentTotalCount = countSnapshot.data().count;

        this.unsubscribeSent = onSnapshot(
          baseQueryWithConstraints,
          (snap) => {
            const docs = snap.docs;
            this.sentItems = docs.map((docSnap) =>
              mapDoc(docSnap as QueryDocumentSnapshot<DocumentData>)
            );
            sentCursors[targetPage] =
              docs.length > 0
                ? (docs[docs.length - 1] as QueryDocumentSnapshot<DocumentData>)
                : undefined;
            this.sentHasMore =
              (targetPage + 1) * pageSize < this.sentTotalCount;
            this.sentPage = targetPage;
            this.sentPageSize = pageSize;
            this.sentLoading = false;
          },
          (error) => {
            console.error("Error listening sent withdrawal requests", error);
            this.sentLoading = false;
          }
        );
      } catch (error) {
        console.error("Error fetching sent withdrawal requests", error);
        this.sentLoading = false;
      }
    },
  },
});
